<template>
  <div
    :id="`c-${comment.id}`"
    class="comment__item"
    :class="showAsDeleted ? 'deleted' : ''"
    :data-id="comment.id"
  >
    <div :class="comment.isNew ? 'is-new' : ''">
      <post-author
        :comment="comment"
      />
      <p
        ref="commentBody"
        class="comment__item__content"
      >
        <span v-if="showAsDeleted">{{ $t('javascript.video.action.conversations.delete_confirmation') }}</span>
        <truncate
          v-else
          :height="60"
          :more="$t('javascript.readmore.more')"
          :less="$t('javascript.readmore.less')"
          :html="processedCommentBody"
          :post-processing-fn="processBody"
          autolink
        />
      </p>

      <div
        v-if="!showAsDeleted"
        class="comment__item__button-group"
      >
        <button
          v-if="!comment.deleted"
          class="vs-btn btn comment__item__recommend"
          :data-id="comment.id"
          @click="executeWithGuard('recommendVideo')"
        >
          <i class="fa fa-thumbs-o-up" />
          {{ likesWithLocale }}
        </button>

        <button
          class="vs-btn btn comment__item__reply"
          :data-reply-to="comment.parent_id ? comment.parent_id : comment.id"
          @click="executeWithGuard('toggleReplyBox')"
        >
          <i class="fa fa-reply" />
          {{ $t('video.action.conversations.reply') }}
        </button>

        <button
          v-if="permission.admin || permission.isDeletable"
          class="vs-btn btn comment__item__delete"
          :data-id="comment.id"
          @click="executeWithGuard('promptForDeletion')"
        >
          <i class="fa fa-trash-o" />
          {{ $t('video.action.conversations.delete.header') }}
        </button>
      </div>
    </div>

    <post-reply-box
      ref="commentReplyBox"
      :user="user"
      :video-id="videoId"
      :reply-id="comment.parent_id ? comment.parent_id : comment.id"
      @success="updateParent"
    />

    <comment-post
      v-for="reply in commentReplies"
      :key="reply.id"
      :comment="reply"
      :user="user"
      :video-id="videoId"
      @success="updateParent"
    />
  </div>
</template>

<script>
// Post components
import PostReplyBox from './PostReplyBox';
import PostAuthor from './PostAuthor';
import Post from './Post';
// Helper components
import truncate from '@/scripts/Components/Utilities/truncate';
// Libraries
import viddsee from '@/scripts/viddsee';
import webcall from '@/scripts/webcall';
import markupHelper from '@/scripts/helpers/markup-helper';
import uiHelper from '@/scripts/helpers/ui-helper';
import Autolinker from 'autolinker';

export default {
  components: {
    PostReplyBox,
    PostAuthor,
    truncate,
  },
  props: {
    comment: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: undefined,
    },
    videoId: {
      type: String,
      required: true,
    },
    isLast: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isMarkedForDeletion: false,
    };
  },
  computed: {
    commentReplies() {
      return (this.comment.replies || []).filter(x => x);
    },

    // Comment body related item
    processedCommentBody() {
      return this.processBody(this.comment.body || '');
    },
    likesWithLocale() {
      return uiHelper.pluralizeLikes(this.comment.likes);
    },

    // UI State
    showAsDeleted() {
      return this.comment.deleted || this.isMarkedForDeletion;
    },
    permission() {
      if (this.user) {
        return {
          isLogin: true,
          admin: this.user.adminAccess,
          isDeletable: this.user.id === this.comment.user.id,
        };
      }
      return {};
    },
  },
  beforeCreate() {
    // See: https://vuejs.org/v2/guide/components.html#Recursive-Components
    this.$options.components['comment-post'] = Post;
  },
  methods: {
    // UI related methods
    toggleReplyBox() {
      if (this.$refs.commentReplyBox) {
        this.$refs.commentReplyBox.toggle();
      }
    },
    promptForDeletion() {
      this.$modal.show('dialog', {
        title: this.$t('video.action.conversations.delete.header'),
        text: this.$t('video.action.conversations.delete.description'),
        buttons: [{
          title: `<i class="fa fa-trash-o" aria-hidden="true"></i> ${
            this.$t('video.action.conversations.delete.button')
          }`,
          handler: () => {
            this
              .deletePost()
              .then(this.$modal.hide('dialog'))
            ;
          },
        }, {
          title: 'Close',
        }],
      });
    },
    deletePost() {
      return webcall.video.deleteComment({
        video_id: this.videoId,
        comment_id: this.comment.id,
      })
        .then((response) => {
          this.isMarkedForDeletion = true;
          this.showDeleteModal = false;
        })
      ;
    },
    recommendVideo() {
      return webcall.video.getCommentVote({
        video_id: this.videoId,
        comment_id: this.comment.id,
      })
        .then((response) => {
          this.comment.likes = response.likes;
        })
      ;
    },
    promptForLogin() {
      ga('send', 'event', 'onboarding', 'comment-login');
      viddsee.ui.modal.register.open({
        callback: 'new_comment',
        loginReason: this.$t('javascript.login-reason.common-community-interaction'),
      });
    },

    // Update related methods
    updateParent(response) {
      this.$emit('success', response);
    },

    // General methods for the component
    executeWithGuard(functionName) {
      if (!this.permission.isLogin) {
        this.promptForLogin();
        return;
      }

      if (typeof this[functionName] === 'function') {
        this[functionName]();
      }
    },
    // For rendering
    processBody(string) {
      const bodyText = markupHelper.unescape(string);
      return Autolinker.link(bodyText.replace(/(?:\r\n|\r|\n)/g, '<br />'));
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/stylesheets/exports/_component-entry';

.is-new {
  background: #FFFFF5;
}

.comment__item__content {
  max-height: initial;
  overflow: initial;
  margin-left: 37px;
}

.comment__item__button-group {
  margin-left: 14px;

  button {
    margin: 0;
    margin-left: $spacer--x3;
    margin-bottom: $spacer;
    padding: 0 0 $spacer;
    font-size: em(12);
    height: 19px;

    opacity: 0.5;
    transition: opacity 0.1s;
    &:hover {
      opacity: 1;
    }
  }
}
</style>
